import React, { useState, useEffect, useContext } from "react";
import PopoverInput from "./inner/popoverInput.jsx";
import PickerFooter from "./inner/pickerFooter.jsx";
import { DayPicker } from "react-day-picker";
import CustomDayButton from "./datepickerComponents/CustomDayButton.jsx";
import { DataSourceContext, IsMobileContext } from "../context/contexts.jsx";
import {
    format,
    startOfTomorrow,
    max,
    min,
    isSameDay,
    startOfMonth,
    endOfMonth,
    addMonths,
    differenceInCalendarDays,
    differenceInCalendarMonths,
    addDays,
    parse,
} from "date-fns";
import Spinner from "./inner/spinner.jsx";

const icon = <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill="currentColor" d="M19 4h-2V3a1 1 0 0 0-2 0v1H9V3a1 1 0 0 0-2 0v1H5a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3m1 15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-7h16Zm0-9H4V7a1 1 0 0 1 1-1h2v1a1 1 0 0 0 2 0V6h6v1a1 1 0 0 0 2 0V6h2a1 1 0 0 1 1 1Z" />
</svg>;

const DATE_MODES = [
    { value: "dates", label: "Calendar" },
    { value: "month", label: "I'm flexible" },
];

// Trip lengths are always described in nights (e.g. "7 nights", "14 nights")
// to stay consistent with how durations are shown elsewhere on the site.
const durationLabel = (nights) => `${nights} ${nights == 1 ? "night" : "nights"}`;

// Booking.com-style fuzzy flexibility: widen the start-date window by ±N days
// around the chosen departure date (the booking URL carries earliest/latest
// start, so this maps directly).
const FLEX_OPTIONS = [
    { value: 0, label: "Exact dates" },
    { value: 1, label: "± 1 day" },
    { value: 2, label: "± 2 days" },
    { value: 3, label: "± 3 days" },
    { value: 7, label: "± 7 days" },
];

function isDateValid(date) {
    if (date == null) {
        return false;
    }
    return !isNaN(date.getTime());
}

// Horizontal chip scroller with arrow controls (booking.com style). The arrows
// sit in their own fixed-width gutters either side of the scroll row (rather
// than overlaying it) so they can never cover the chips/night labels. The
// gutters are always reserved to keep the layout stable as the arrows toggle.
const HScroller = ({ children }) => {
    const ref = React.useRef(null);
    const [canLeft, setCanLeft] = React.useState(false);
    const [canRight, setCanRight] = React.useState(false);
    const update = () => {
        const el = ref.current;
        if (!el) return;
        setCanLeft(el.scrollLeft > 4);
        setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    React.useEffect(update);
    const scrollByDir = (dir) => {
        const el = ref.current;
        if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
    };
    const slot = "shrink-0 w-8 flex justify-center";
    const arrow = "w-8 h-8 flex items-center justify-center rounded-full bg-white border border-content_4 shadow-md text-primary hover:border-emp text-xl leading-none";
    return (
        <div className="flex items-center gap-1">
            <div className={slot}>
                {canLeft && (
                    <button type="button" aria-label="Scroll left" onClick={() => scrollByDir(-1)} className={arrow}>&#8249;</button>
                )}
            </div>
            <div ref={ref} onScroll={update} className="flex gap-2 overflow-x-auto scrollbar-hide py-1 flex-1 min-w-0">
                {children}
            </div>
            <div className={slot}>
                {canRight && (
                    <button type="button" aria-label="Scroll right" onClick={() => scrollByDir(1)} className={arrow}>&#8250;</button>
                )}
            </div>
        </div>
    );
};

const DatePicker = ({ departureAirports, destination, selectedFrom, selectedTo, onChange, dateMode = "dates", onDateModeChange = () => { }, selectedDuration = null, onDurationChange = () => { }, flexDays = 0, onFlexDaysChange = () => { }, error }) => {

    const [isOpen, setIsOpen] = useState(false);
    const [enabledDays, setEnabledDays] = useState([]);
    const [dayPricing, setDayPricing] = useState([]);
    // Distinguish "still fetching" from "fetched, but nothing available" so the
    // loading spinner can't run forever when a destination has no dates (or the
    // request fails and degrades to an empty result).
    const [datesLoaded, setDatesLoaded] = useState(false);

    const dataSource = useContext(DataSourceContext);
    const [availableReturnDates, setAvailableReturnDates] = useState();
    const [availableDurations, setAvailableDurations] = useState(null);
    const [activeMonth, setActiveMonth] = useState(null);
    // The day currently under the cursor, used to preview the hover state.
    const [hoveredDay, setHoveredDay] = useState(null);

    const isMobile = useContext(IsMobileContext);

    // The calendar must never reveal months in the past. Stale (test) data can
    // include dates that have already been, which would otherwise open the
    // calendar on a past month - clamp every "earliest visible month" to the
    // start of the current month. Past days within the current month stay
    // disabled via the matcher below, so nothing bookable is hidden.
    const earliestMonth = startOfMonth(new Date());
    const clampMonth = (date) => max([earliestMonth, date]);

    // Stable day-button renderer. Defining it inline in the DayPicker would give
    // it a new identity on every render, so a hover-driven re-render would
    // remount the buttons and swallow the in-flight click/tap. Keep its identity
    // tied only to data that genuinely changes how a day renders.
    const renderDayButton = React.useCallback((props) => {
        const datestring = format(props.day.date, "yyyy-MM-dd");
        const price = dayPricing[datestring];
        return CustomDayButton(props, price, !(selectedFrom && !selectedTo));
    }, [dayPricing, selectedFrom, selectedTo]);
    const dayComponents = React.useMemo(() => ({ DayButton: renderDayButton }), [renderDayButton]);

    useEffect(() => {
        if (enabledDays && enabledDays.length > 0) {
            let initialMonth = selectedFrom ? selectedFrom : clampMonth(min(enabledDays));
            setActiveMonth(initialMonth);
        }
    }, [selectedFrom, selectedTo, enabledDays]);

    useEffect(() => {
        async function fetchData() {
            setEnabledDays([]);
            setDayPricing({});
            setDatesLoaded(false);
            if (destination && departureAirports.length > 0) {
                var destinations = await dataSource.getAvailableDates(departureAirports, destination);
                var days = Object.keys(destinations);
                setEnabledDays(days);
                setDayPricing(destinations);
                setDatesLoaded(true);
            }else{
                onChange(null, null);

            }
        }
        fetchData();
    }, [departureAirports, destination]);

    // In month mode the booking engine still needs a duration, so fetch the
    // durations that actually exist somewhere inside the selected month.
    useEffect(() => {
        async function fetchData() {
            setAvailableDurations(null);
            if (dateMode == "month" && selectedFrom && selectedTo && destination && departureAirports.length > 0) {
                var durations = await dataSource.getDurationsForRange(departureAirports, destination, selectedFrom, selectedTo);
                setAvailableDurations(durations);
                if (selectedDuration && !durations.some((x) => x.duration == selectedDuration)) {
                    onDurationChange(null);
                }
            }
        }
        fetchData();
    }, [dateMode, destination, departureAirports, selectedFrom?.getTime(), selectedTo?.getTime()]);

    const handleDayClick = (day, modifiers) => {
        if (!selectedFrom || (selectedFrom && selectedTo)) {
            onChange(day, null);
            async function fetchData() {
                var durations = await dataSource.getReturnDates(departureAirports, destination, day);
                var returnDates = durations.map((x, i) => addDays(day, x));
                setAvailableReturnDates(returnDates);
            }
            fetchData();
        } else {
            onChange(selectedFrom, day);
        }
    };

    const handleModeClick = (mode) => {
        if (mode == dateMode) {
            return;
        }
        onDateModeChange(mode);
        onChange(null, null);
        onDurationChange(null);
        setAvailableReturnDates(undefined);
        setAvailableDurations(null);
    };

    const handleMonthClick = (monthKey) => {
        var monthStart = parse(monthKey + "-01", "yyyy-MM-dd", new Date());
        // Clamp the window start to tomorrow when the current month is picked.
        var from = max([monthStart, startOfTomorrow()]);
        onChange(from, endOfMonth(monthStart));
    };

    const onMonthChange = (day) => {
        setActiveMonth(day);
    };
    const getTextboxText = () => {
        if (dateMode == "month") {
            if (!selectedFrom) {
                return null;
            }
            var monthStr = format(selectedFrom, "MMMM yyyy");
            return selectedDuration ? `${monthStr} - ${selectedDuration} nights` : monthStr;
        }
        if (!selectedFrom && !selectedTo) {
            return null;
        }
        // Drop the year from the departure date when both dates share it -
        // the full "dd/MM/yyyy - dd/MM/yyyy · N nights" string overflows the input.
        const sameYear = selectedFrom && selectedTo && selectedFrom.getFullYear() == selectedTo.getFullYear();
        var str = `${selectedFrom ? format(selectedFrom, sameYear ? "dd/MM" : "dd/MM/yyyy") : ""} - ${selectedTo ? format(selectedTo, "dd/MM/yyyy") : ""}`;
        if (selectedFrom && selectedTo) {
            const nights = differenceInCalendarDays(selectedTo, selectedFrom);
            str += ` · ${nights} ${nights == 1 ? "night" : "nights"}`;
        }
        if (flexDays > 0) {
            str += ` (± ${flexDays} ${flexDays == 1 ? "day" : "days"})`;
        }
        return str;

    };

    function formatWeekdayName(weekday, options) {
        return format(weekday, "iii", options);
    }

    // Group the available days into months for the "whole month" view. Days
    // before tomorrow can't be searched, so they don't count towards a month.
    const tomorrowString = format(startOfTomorrow(), "yyyy-MM-dd");
    const monthsByKey = {};
    const monthSummaries = [];
    for (const dayString of Object.keys(dayPricing)) {
        if (dayString < tomorrowString) {
            continue;
        }
        const monthKey = dayString.slice(0, 7);
        if (!monthsByKey[monthKey]) {
            monthsByKey[monthKey] = { key: monthKey, minPrice: null };
            monthSummaries.push(monthsByKey[monthKey]);
        }
        const price = dayPricing[dayString]?.price;
        if (price != null && (monthsByKey[monthKey].minPrice == null || price < monthsByKey[monthKey].minPrice)) {
            monthsByKey[monthKey].minPrice = price;
        }
    }
    monthSummaries.sort((a, b) => a.key.localeCompare(b.key));

    // Build a continuous run of months from the current month through the last
    // available one, so any month with no availability shows up greyed out
    // (booking.com style) rather than simply being omitted.
    const allMonths = [];
    if (monthSummaries.length > 0) {
        let cursor = startOfMonth(new Date());
        const lastMonthDate = parse(monthSummaries[monthSummaries.length - 1].key + "-01", "yyyy-MM-dd", new Date());
        while (cursor <= lastMonthDate) {
            const key = format(cursor, "yyyy-MM");
            allMonths.push(monthsByKey[key] ?? { key, minPrice: null, unavailable: true });
            cursor = addMonths(cursor, 1);
        }
    }

    const selectedMonthKey = dateMode == "month" && selectedFrom ? format(selectedFrom, "yyyy-MM") : null;

    // A single "when do you want to go?" month card. Shared between the desktop
    // horizontal scroller and the mobile vertical grid (which scrolls down like
    // the calendar) - only the width class differs.
    const renderMonthCard = (month, widthClass) => {
        const monthDate = parse(month.key + "-01", "yyyy-MM-dd", new Date());
        const isSelected = selectedMonthKey == month.key;
        const isUnavailable = !!month.unavailable;
        return (
            <button key={month.key} type="button" disabled={isUnavailable}
                onClick={isUnavailable ? undefined : () => handleMonthClick(month.key)}
                className={widthClass + " rounded-[12px] border px-2 py-2 text-center transition-colors " +
                    (isUnavailable
                        ? "border-content_5 bg-content_5/30 text-content_2 opacity-40 cursor-not-allowed"
                        : isSelected
                            ? "border-emp bg-ocean-100 text-primary"
                            : "border-content_4 hover:border-emp")}>
                <div className="font-semibold leading-tight">{format(monthDate, "MMM")}</div>
                <div className="text-xs text-content_2 leading-tight">{format(monthDate, "yyyy")}</div>
                <span className="text-xs font-bold whitespace-nowrap">{isUnavailable ? "Unavailable" : (month.minPrice != null ? `from £${month.minPrice}` : " ")}</span>
            </button>
        );
    };

    let displayFrom = clampMonth(min(enabledDays));
    let displayTo = max([displayFrom, max(enabledDays)]);
    let footer;

    // Range-selection state used by the calendars below.
    let selected = undefined;
    let selectingState = "outbound";
    if (selectedFrom && !selectedTo) {
        // Outbound chosen, now picking the return.
        selected = { from: selectedFrom, to: undefined };
        selectingState = "inbound";
        if (availableReturnDates) {
            displayFrom = min(availableReturnDates);
            displayTo = max(availableReturnDates);
        }
    } else if (selectedFrom && selectedTo) {
        selected = { from: selectedFrom, to: selectedTo };
    }

    const activeMonthToUse = activeMonth;

    displayTo = addMonths(displayTo, 2);

    // Mobile renders every available month stacked in one continuous scroll
    // (like booking.com), so it needs the full month span regardless of the
    // return-date narrowing above.
    const scrollFromMonth = enabledDays.length > 0 ? clampMonth(startOfMonth(min(enabledDays))) : earliestMonth;
    const scrollToMonth = enabledDays.length > 0 ? max([scrollFromMonth, startOfMonth(max(enabledDays))]) : scrollFromMonth;
    const scrollMonthCount = Math.max(1, differenceInCalendarMonths(scrollToMonth, scrollFromMonth) + 1);

    const getDisabledDays = (day) => {
        if (selectedFrom && !selectedTo) {
            if (availableReturnDates) {
                for (let entry of availableReturnDates) {
                    var hasMatch = isSameDay(entry, day);
                    if (hasMatch) {
                        return false;
                    }
                }
                return true;
            }
            return false;
        } else {
            return enabledDays ? !enabledDays.some((disabledDay) => isSameDay(day, disabledDay)) : true;
        }
    };

    const disabledMatcher = [
        { before: startOfTomorrow() },
        getDisabledDays
    ];

    // State-aware hover preview:
    //  - nothing selected -> highlight the day that would become the departure
    //  - departure only    -> preview the whole would-be range up to the cursor
    //  - both selected     -> show that clicking starts a fresh departure
    const hoverModifiers = {};
    if (hoveredDay) {
        if (selectingState == "inbound" && selectedFrom && differenceInCalendarDays(hoveredDay, selectedFrom) > 0) {
            hoverModifiers.hoverRange = (day) => differenceInCalendarDays(day, selectedFrom) > 0 && differenceInCalendarDays(hoveredDay, day) > 0;
            hoverModifiers.hoverEnd = (day) => isSameDay(day, hoveredDay);
        } else if (selectedFrom && selectedTo) {
            hoverModifiers.hoverReset = (day) => isSameDay(day, hoveredDay);
        } else if (!selectedFrom) {
            hoverModifiers.hoverFirst = (day) => isSameDay(day, hoveredDay);
        }
    }

    // Shared Clear / Ok footer (both modes). Clear resets the dates, duration
    // and fuzziness; Ok just closes the popover.
    const clearDates = () => {
        onChange(null, null);
        onDurationChange(null);
        onFlexDaysChange(0);
    };
    // Incomplete selection: a departure with no return (calendar), or a month
    // with no duration (flexible). Ok is blocked until the selection is whole.
    const isIncomplete = dateMode == "dates"
        ? (selectedFrom && !selectedTo)
        : (selectedFrom && !selectedDuration);
    footer = <PickerFooter onClear={clearDates} onConfirm={() => setIsOpen(false)}
        clearDisabled={!selectedFrom && !selectedTo && !selectedDuration && !flexDays}
        confirmDisabled={isIncomplete} />;


    return <PopoverInput error={error} footer={footer} panelMaxHeight={800} doubleWidth={true} disabled={departureAirports.length == 0 || destination == null || destination == ""} icon={icon} inputText={"Select Dates"} isLoading={false} filterText={getTextboxText()} isOpen={isOpen} setIsOpen={setIsOpen}>
        {/* Pin a fixed desktop width so both views share it - the calendar
            (two months) fits comfortably and the "I'm flexible" month strip
            scrolls within it instead of stretching the popover. Mobile is a
            full-width sheet, so it stays fluid. */}
        <div className="text-center" style={isMobile ? undefined : { width: "43rem" }}>
            <div className="flex mb-4 border-b border-content_4" role="tablist">
                {DATE_MODES.map((mode) => (
                    <button key={mode.value} type="button" role="tab"
                        aria-selected={dateMode == mode.value}
                        onClick={() => handleModeClick(mode.value)}
                        className={"flex-1 px-4 py-3 text-base font-semibold border-b-2 -mb-px transition-colors " +
                            (dateMode == mode.value ? "border-emp text-emp" : "border-transparent text-content_2 hover:text-primary")}>
                        {mode.label}
                    </button>
                ))}
            </div>
            {enabledDays.length == 0 && !datesLoaded && (
                <Spinner size={40} label="Loading" />
            )}
            {enabledDays.length == 0 && datesLoaded && (
                <div className="text-content_2 text-sm py-8 px-4 text-center">
                    No available dates for this selection. Try a different destination or departure airport.
                </div>
            )}
            {enabledDays.length > 0 && dateMode == "month" && (
                <div className="text-left">
                    {/* Booking.com-style "I'm flexible" panel: pick a month, then how
                        many nights, both visible at once. Single-select so it still
                        maps to one booking-engine window + duration. */}
                    <div className="mb-4">
                        <div className="font-semibold mb-2">When do you want to go?</div>
                        {isMobile ? (
                            // Mobile: months wrap into a grid that scrolls down
                            // within the sheet, matching the calendar's vertical
                            // scroll rather than a sideways swipe.
                            <div className="grid grid-cols-3 gap-2">
                                {allMonths.map((month) => renderMonthCard(month, "w-full"))}
                            </div>
                        ) : (
                        <HScroller>
                            {allMonths.map((month) => {
                                const monthDate = parse(month.key + "-01", "yyyy-MM-dd", new Date());
                                const isSelected = selectedMonthKey == month.key;
                                const isUnavailable = !!month.unavailable;
                                return (
                                    <button key={month.key} type="button" disabled={isUnavailable}
                                        onClick={isUnavailable ? undefined : () => handleMonthClick(month.key)}
                                        className={"shrink-0 w-[88px] rounded-[12px] border px-2 py-2 text-center transition-colors " +
                                            (isUnavailable
                                                ? "border-content_5 bg-content_5/30 text-content_2 opacity-40 cursor-not-allowed"
                                                : isSelected
                                                    ? "border-emp bg-ocean-100 text-primary"
                                                    : "border-content_4 hover:border-emp")}>
                                        <div className="font-semibold leading-tight">{format(monthDate, "MMM")}</div>
                                        <div className="text-xs text-content_2 leading-tight">{format(monthDate, "yyyy")}</div>
                                        <span className="text-xs font-bold whitespace-nowrap">{isUnavailable ? "Unavailable" : (month.minPrice != null ? `from £${month.minPrice}` : " ")}</span>
                                    </button>
                                );
                            })}
                        </HScroller>
                        )}
                    </div>
                    <div>
                        <div className="font-semibold mb-2">How long do you want to stay?</div>
                        {!selectedMonthKey && (
                            <div className="text-sm text-content_2 py-2">Pick a month above to see available trip lengths.</div>
                        )}
                        {selectedMonthKey && availableDurations == null && (
                            <div className="py-2"><Spinner size={20} /></div>
                        )}
                        {selectedMonthKey && availableDurations && availableDurations.length == 0 && (
                            <div className="text-sm text-content_2 py-2">No availability for this month.</div>
                        )}
                        {selectedMonthKey && availableDurations && availableDurations.length > 0 && (
                            <HScroller>
                                {availableDurations.map((option) => {
                                    const isSelected = selectedDuration == option.duration;
                                    return (
                                        <button key={option.duration} type="button"
                                            onClick={() => onDurationChange(option.duration)}
                                            title={option.price != null
                                                ? `${option.duration} nights · £${Math.round(option.price / option.duration)} per night`
                                                : `${option.duration} nights`}
                                            className={"shrink-0 whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-colors " +
                                                (isSelected ? "border-emp bg-emp text-white" : "border-content_4 hover:border-emp")}>
                                            <span className="font-semibold">{durationLabel(option.duration)}</span>
                                            {option.price != null && (
                                                <span className={"ml-2 text-xs font-bold " + (isSelected ? "text-white/90" : "text-content_2")}>from £{option.price}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </HScroller>
                        )}
                    </div>
                </div>
            )}
            {enabledDays.length > 0 && dateMode == "dates" && (
                <div>
                    {/* Prompt the user through the two-step range selection now that
                        there's no footer to do it. */}
                    <div className="mb-2 text-sm font-medium text-center">
                        {!selectedFrom && (
                            <span className="text-content_2">Select your departure date</span>
                        )}
                        {selectedFrom && !selectedTo && (
                            hoveredDay && differenceInCalendarDays(hoveredDay, selectedFrom) > 0
                                ? <span className="text-emp">{differenceInCalendarDays(hoveredDay, selectedFrom)} {differenceInCalendarDays(hoveredDay, selectedFrom) == 1 ? "night" : "nights"} — click to confirm your return date</span>
                                : <span className="text-emp">Now select your return date</span>
                        )}
                        {selectedFrom && selectedTo && (
                            <span className="text-content_2">{differenceInCalendarDays(selectedTo, selectedFrom)} {differenceInCalendarDays(selectedTo, selectedFrom) == 1 ? "night" : "nights"} selected</span>
                        )}
                    </div>
                    {/* Key for the green price-indicator dot shown on the
                        cheapest days in the calendar below. */}
                    <div className="flex items-center justify-center gap-1.5 text-xs text-content_2 mb-3">
                        <span className="inline-block w-2 h-2 rounded-full bg-green-300"></span>
                        <span>Marks the lowest prices</span>
                    </div>
                    {/* On mobile the calendar is a long scroll, so expose the
                        flexibility as a dropdown pinned to the top rather than
                        chips the user would have to scroll to the bottom for. */}
                    {isMobile && (
                        <div className="sticky top-0 z-10 bg-white pb-2 mb-2 border-b border-content_4 text-left">
                            <label className="block text-sm font-semibold mb-1">How flexible are your dates?</label>
                            <select className="form-input" value={flexDays} onChange={(e) => onFlexDaysChange(parseInt(e.target.value, 10))}>
                                {FLEX_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {isMobile ? (
                        // Mobile: every available month stacked in one continuous
                        // vertical scroll (no paging), like booking.com.
                        <DayPicker
                            ISOWeek
                            mode="range"
                            hideNavigation
                            weekStartsOn={1}
                            startMonth={scrollFromMonth}
                            endMonth={scrollToMonth}
                            numberOfMonths={scrollMonthCount}
                            defaultMonth={selectedFrom ?? scrollFromMonth}
                            selected={selected}
                            // Selection is driven entirely by our own state via
                            // onDayClick; passing onSelect (even a no-op) keeps
                            // `selected` controlled so clearing it to undefined
                            // actually repaints. Without it react-day-picker keeps
                            // its own internal selection and Clear leaves the
                            // highlight on screen (mobile shows every month at once
                            // so the stale range stays visible).
                            onSelect={() => { }}
                            disabled={disabledMatcher}
                            onDayClick={handleDayClick}
                            components={dayComponents}
                            formatters={{
                                formatWeekdayName: formatWeekdayName,
                            }}
                            classNames={{
                                "months": "rdp-months flex-col gap-6"
                            }}
                        />
                    ) : (
                        // Desktop: two months side-by-side with paged navigation.
                        <DayPicker
                            ISOWeek
                            mode="range"
                            captionLayout="dropdown"
                            animate
                            navLayout="around"
                            weekStartsOn={1}
                            startMonth={displayFrom}
                            endMonth={displayTo}
                            numberOfMonths={2}
                            defaultMonth={activeMonth}
                            month={activeMonthToUse}
                            selected={selected}
                            // See the mobile calendar above: onSelect keeps
                            // `selected` controlled so Clear repaints reliably.
                            onSelect={() => { }}
                            disabled={disabledMatcher}
                            modifiers={hoverModifiers}
                            onDayClick={handleDayClick}
                            onDayMouseEnter={(day, m) => setHoveredDay(m?.disabled ? null : day)}
                            onDayMouseLeave={() => setHoveredDay(null)}
                            onMonthChange={onMonthChange}
                            components={dayComponents}
                            formatters={{
                                formatWeekdayName: formatWeekdayName,
                            }}
                            classNames={{
                                // Force the two months side-by-side (the default
                                // rdp-months wraps).
                                "months": "rdp-months justify-center flex-nowrap"
                            }}
                        />
                    )}
                    {/* Fuzzy date flexibility - widens the start-date window sent to
                        the booking engine. Desktop shows chips under the calendar;
                        mobile uses the pinned dropdown above. */}
                    {!isMobile && (
                        <div className="mt-3 pt-3 border-t border-content_4 text-left">
                            <div className="text-sm font-semibold mb-2">How flexible are your dates?</div>
                            <div className="flex flex-wrap gap-2">
                                {FLEX_OPTIONS.map((opt) => (
                                    <button key={opt.value} type="button"
                                        onClick={() => onFlexDaysChange(opt.value)}
                                        className={"rounded-full border px-3 py-1.5 text-sm transition-colors " +
                                            (flexDays == opt.value ? "border-emp bg-emp text-white" : "border-content_4 hover:border-emp")}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    </PopoverInput>
}
export default DatePicker;
